import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useState, useEffect, useRef } from 'react';
import { ref, get, set, update } from 'firebase/database';
import { database } from '../../firebase';
import { removeConnectionOptimizedBulk } from './DatabaseOptimizer';
import { emitAtsAnalyticsEvent } from '../../services/atsAnalyticsService';
import { useTranslation } from 'react-i18next';
import ApplicantCell from './ApplicantCell';

const SubstageArea = ({
  substageId,
  substageType = 'static',
  substageAssessmentId = null,
  title,
  icon,
  applicants,
  onDragOver,
  onDrop,
  onOpenApplicantPopup,
  applicantCount,
  selectedApplicants = [],
  isOfferApprover,
  pendingApprovals,
  applicantStatusMap = {},
  // inline menu props
  userUID,
  jobId,
  substageData: substageDataProp = null,
  onSubstageUpdated,
  onSubstageDeleted,
  onMultipleApplicantsRemoved,
  isMenuSupported = false,
}) => {
  useUiLanguage();
  const { t } = useTranslation('applicants');
  const [menuOpen, setMenuOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState(title || '');
  const [substageData, setSubstageData] = useState(substageDataProp);
  const menuRef = useRef(null);

  useEffect(() => {
    if (substageDataProp) setSubstageData(substageDataProp);
  }, [substageDataProp]);

  // Load substage data if not provided and menu is supported
  useEffect(() => {
    if (!isMenuSupported || substageDataProp || !substageId || !userUID) return;
    if (substageId.startsWith('all-applicants') || substageId === 'filter-applicants') return;
    const load = async () => {
      try {
        const workflowRef = ref(database, `companies/${userUID}/hiringWorkflow`);
        const snap = await get(workflowRef);
        const workflow = snap.val();
        if (workflow) {
          Object.values(workflow).forEach(stage => {
            if (stage.substages && stage.substages[substageId]) {
              setSubstageData(stage.substages[substageId]);
            }
          });
        }
      } catch (e) {
        console.error('Error loading substage data:', e);
      }
    };
    load();
  }, [substageId, userUID, isMenuSupported, substageDataProp]);

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
        setRenaming(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const handleDrop = (e) => {
    e.preventDefault();
    onDrop(e);
  };

  const handleDragStart = (e, applicantId) => {
    e.dataTransfer.setData("text/plain", applicantId);

    if (selectedApplicants.includes(applicantId) && selectedApplicants.length > 1) {
      const selectedApplicantNames = applicants
        .filter(app => selectedApplicants.includes(app.id))
        .map(app => app.name || app.username || 'Unknown');

      const dragPreview = document.createElement('div');
      dragPreview.style.position = 'absolute';
      dragPreview.style.top = '-1000px';
      dragPreview.style.left = '-1000px';
      dragPreview.style.display = 'flex';
      dragPreview.style.flexDirection = 'column';
      dragPreview.style.gap = '4px';
      dragPreview.style.pointerEvents = 'none';

      const maxNameCards = 2;
      const nameCardsToShow = Math.min(maxNameCards, selectedApplicantNames.length);
      const totalCards = 1 + nameCardsToShow;

      for (let i = 0; i < totalCards; i++) {
        const card = document.createElement('div');
        card.dataset.lightControl = 'filled';
        card.style.backgroundColor = '#568ded';
        card.style.color = 'var(--app-text, #FFFFFF)';
        card.style.padding = i === 0 ? '10px 16px' : '8px 14px';
        card.style.borderRadius = '8px';
        card.style.fontFamily = 'Montserrat, sans-serif';
        card.style.fontSize = i === 0 ? '15px' : '13px';
        card.style.fontWeight = '600';
        card.style.boxShadow = '0 3px 10px rgba(0,0,0,0.3)';
        card.style.minWidth = '160px';
        card.style.textAlign = 'center';
        card.style.opacity = (1 - (i * 0.2)).toString();
        card.style.transform = `translateX(${i * 5}px) translateY(${i * 5}px)`;
        card.style.whiteSpace = 'nowrap';
        card.style.overflow = 'hidden';
        card.style.textOverflow = 'ellipsis';

        if (i === 0) {
          card.innerHTML = `
            <div style="display: flex; align-items: center; justify-content: center; gap: 10px;">
              <i class="fa-solid fa-people-group" style="font-size: 18px;"></i>
              <span style="font-size: 18px; font-weight: var(--app-weight-strong, 700);">${selectedApplicants.length}</span>
              <span style="font-size: 15px; opacity: 0.9;">Candidates</span>
            </div>
          `;
        } else {
          const nameIndex = i - 1;
          if (nameIndex < selectedApplicantNames.length) {
            const name = selectedApplicantNames[nameIndex];
            card.textContent = name.split(' ')[0];
          } else {
            card.textContent = '•••';
          }
        }

        dragPreview.appendChild(card);
      }

      const additionalCandidates = selectedApplicants.length - nameCardsToShow;
      if (additionalCandidates > 0) {
        const moreIndicator = document.createElement('div');
        moreIndicator.dataset.lightControl = 'filled';
        moreIndicator.style.backgroundColor = 'rgba(86, 141, 237, 0.7)';
        moreIndicator.style.color = 'var(--app-text, #FFFFFF)';
        moreIndicator.style.padding = '6px 12px';
        moreIndicator.style.borderRadius = '6px';
        moreIndicator.style.fontFamily = 'Montserrat, sans-serif';
        moreIndicator.style.fontSize = '12px';
        moreIndicator.style.fontWeight = '600';
        moreIndicator.style.textAlign = 'center';
        moreIndicator.style.transform = `translateX(${totalCards * 5}px) translateY(${totalCards * 5}px)`;
        moreIndicator.textContent = `+${additionalCandidates} more`;
        dragPreview.appendChild(moreIndicator);
      }

      document.body.appendChild(dragPreview);
      e.dataTransfer.setDragImage(dragPreview, 80, 25);

      setTimeout(() => {
        document.body.removeChild(dragPreview);
      }, 0);
    }
  };

  // --- Inline menu actions ---

  const isAllApplicants = substageId && substageId.startsWith('all-applicants');
  const isFilterApplicants = substageId === 'filter-applicants';

  const handleRename = async () => {
    if (!newName.trim()) {
      alert(t('alerts.enterSubstageName'));
      return;
    }
    try {
      const workflowRef = ref(database, `companies/${userUID}/hiringWorkflow`);
      const snap = await get(workflowRef);
      const workflow = snap.val();
      if (workflow) {
        Object.keys(workflow).forEach(stageKey => {
          const stage = workflow[stageKey];
          if (stage.substages && stage.substages[substageId]) {
            set(ref(database, `companies/${userUID}/hiringWorkflow/${stageKey}/substages/${substageId}/name`), newName.trim());
          }
        });
      }
      if (onSubstageUpdated) onSubstageUpdated(substageId, newName.trim());
      setMenuOpen(false);
      setRenaming(false);
    } catch (e) {
      console.error('Error renaming substage:', e);
      alert(t('alerts.savingError'));
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('editSubstage.confirmDelete'))) return;
    try {
      const companyRef = ref(database, `companies/${userUID}`);
      const snap = await get(companyRef);
      const companyData = snap.val() || {};
      const workflow = companyData.hiringWorkflow;
      const updates = {};
      if (workflow) {
        Object.keys(workflow).forEach(stageKey => {
          const stage = workflow[stageKey];
          if (stage.substages && stage.substages[substageId]) {
            updates[`companies/${userUID}/hiringWorkflow/${stageKey}/substages/${substageId}`] = null;
          }
        });
      }
      if (companyData.assessmentAutomation?.substageId === substageId) {
        updates[`companies/${userUID}/assessmentAutomation`] = {
          ...companyData.assessmentAutomation,
          enabled: false,
          updatedAt: Date.now()
        };
      }
      await update(ref(database), updates);
      if (onSubstageDeleted) onSubstageDeleted(substageId);
      setMenuOpen(false);
    } catch (e) {
      console.error('Error deleting substage:', e);
      alert(t('alerts.savingError'));
    }
  };

  const handleRejectAll = async () => {
    if (!window.confirm(t('editSubstage.confirmRejectAll'))) return;
    try {
      const employerIndexRef = ref(database, `companies/${userUID}/applicant_index/by_job`);
      const employerIndexSnap = await get(employerIndexRef);

      if (!employerIndexSnap.exists()) {
        alert(t('editSubstage.noApplicants'));
        return;
      }

      const jobsData = employerIndexSnap.val();
      const connectionsToRemove = [];
      const rejectionPayloads = [];
      const rejectedApplicantIds = [];

      if (jobsData[jobId]) {
        Object.entries(jobsData[jobId]).forEach(([connectionKey, applicantData]) => {
          const shouldReject = isAllApplicants
            ? (applicantData.substageId === substageId || !applicantData.substageId || applicantData.substageId === 'all-applicants-1')
            : (applicantData.substageId === substageId);

          if (shouldReject) {
            connectionsToRemove.push(connectionKey);
            rejectedApplicantIds.push(applicantData.applicantId);
            rejectionPayloads.push({
              connectionKey,
              applicantId: applicantData.applicantId,
              applicantType: applicantData.applicantType || (String(applicantData.applicantId || '').startsWith('temp_') ? 'imported' : 'authenticated'),
              jobTitle: applicantData.jobTitle || '',
              stageId: applicantData.stageId || 'stage-1',
              substageId: applicantData.substageId || substageId
            });
          }
        });
      }

      if (connectionsToRemove.length === 0) {
        alert(t('editSubstage.noApplicants'));
        return;
      }

      const customListDecrements = {};
      for (const connectionKey of connectionsToRemove) {
        try {
          const connectionSnap = await get(ref(database, `connections/${connectionKey}`));
          if (connectionSnap.exists()) {
            const connectionData = connectionSnap.val();
            const employerUid = connectionData.employerUid || connectionData.employerId;
            const cJobId = connectionData.jobId;
            if (connectionData.isCustomList && employerUid && cJobId) {
              const listKey = `${employerUid}:::${cJobId}`;
              customListDecrements[listKey] = (customListDecrements[listKey] || 0) + 1;
            }
          }
        } catch (err) {
          console.warn('Error checking connection for custom list count:', connectionKey, err);
        }
      }

      await Promise.allSettled(
        rejectionPayloads.map((payload) =>
          emitAtsAnalyticsEvent({
            company_uid: userUID,
            event_type: 'candidate_rejected',
            occurred_at: new Date().toISOString(),
            subject: {
              applicantId: payload.applicantId,
              connectionKey: payload.connectionKey,
              applicantType: payload.applicantType,
              outcomeState: 'rejected'
            },
            job: { jobId: jobId || '', jobTitle: payload.jobTitle },
            context: {
              originAction: 'bulk_reject_substage',
              stageId: payload.stageId,
              substageId: payload.substageId
            }
          })
        )
      );

      await Promise.all(connectionsToRemove.map(k => removeConnectionOptimizedBulk(k)));

      const customListUpdates = {};
      for (const [listKey, decrementAmount] of Object.entries(customListDecrements)) {
        const [employerUid, cJobId] = listKey.split(':::');
        try {
          const customListSnap = await get(ref(database, `companies/${employerUid}/custom_lists/${cJobId}`));
          if (customListSnap.exists()) {
            const customListData = customListSnap.val();
            const currentCount = customListData.applicantCount || 0;
            const newCount = Math.max(0, currentCount - decrementAmount);
            customListUpdates[`companies/${employerUid}/custom_lists/${cJobId}/applicantCount`] = newCount;
            customListUpdates[`companies/${employerUid}/custom_lists/${cJobId}/lastUpdated`] = Date.now();
          }
        } catch (err) {
          console.error('Error calculating custom list update:', listKey, err);
        }
      }

      if (Object.keys(customListUpdates).length > 0) {
        await update(ref(database), customListUpdates);
      }

      if (onMultipleApplicantsRemoved && rejectedApplicantIds.length > 0) {
        onMultipleApplicantsRemoved(rejectedApplicantIds);
      }

      alert(t('editSubstage.rejected', { count: connectionsToRemove.length }));
      setMenuOpen(false);
    } catch (err) {
      console.error('Error rejecting all applicants:', err);
      alert(t('alerts.savingError'));
    }
  };

  const handleEditAssessment = () => {
    const link = substageData?.link || substageAssessmentId;
    if (link) {
      const editor = substageData?.deliveryType === 'two-way-voice' ? 'interview-editor' : 'assessment-editor';
      window.open(`/${editor}/${link}`, '_blank');
    } else {
      alert(t('alerts.noAssessmentContent'));
    }
    setMenuOpen(false);
  };

  const handleToggleAutoScreening = async () => {
    if (!userUID || substageData?.type !== 'ai-assessment' || !substageData?.link) return;
    const nextEnabled = substageData.autoScreeningEnabled !== true;
    try {
      const companyRef = ref(database, `companies/${userUID}`);
      const companySnap = await get(companyRef);
      const companyData = companySnap.val() || {};
      const workflow = companyData.hiringWorkflow || {};
      let stageId = 'stage-1';
      Object.entries(workflow).some(([candidateStageId, stage]) => {
        if ((stage?.substages || {})[substageId]) {
          stageId = candidateStageId;
          return true;
        }
        return false;
      });

      const updates = {
        [`companies/${userUID}/hiringWorkflow/${stageId}/substages/${substageId}/autoScreeningEnabled`]: nextEnabled
      };
      const previous = companyData.assessmentAutomation || {};

      if (nextEnabled) {
        const previousStageId = previous.stageId || 'stage-1';
        const previousStillExists = Boolean(
          workflow?.[previousStageId]?.substages?.[previous.substageId]
        );
        if (previous.substageId && previous.substageId !== substageId && previousStillExists) {
          updates[`companies/${userUID}/hiringWorkflow/${previousStageId}/substages/${previous.substageId}/autoScreeningEnabled`] = false;
        }
        updates[`companies/${userUID}/hiringWorkflow/${stageId}/substages/${substageId}/afterId`] = 'filter-applicants';
        updates[`companies/${userUID}/assessmentAutomation`] = {
          enabled: true,
          stageId,
          substageId,
          assessmentId: substageData.link,
          updatedAt: Date.now()
        };
      } else if (previous.substageId === substageId) {
        updates[`companies/${userUID}/assessmentAutomation`] = {
          enabled: false,
          stageId,
          substageId,
          assessmentId: substageData.link,
          updatedAt: Date.now()
        };
      }

      await update(ref(database), updates);
      setSubstageData(current => ({ ...current, autoScreeningEnabled: nextEnabled }));
    } catch (error) {
      console.error('Error updating assessment auto-screening:', error);
      alert(t('alerts.savingError'));
    }
  };

  return (
    <div
      id={substageId}
      className="substage-area"
      data-substage-id={substageId}
      data-tutorial-substage={substageId}
      onDragOver={onDragOver}
      onDrop={handleDrop}
    >
      <div className="substage-hdr">
        <div className="substage-hdr-left">
          <div className="substage-hdr-title">
            <i className={icon || 'fa-solid fa-people-group'}></i>
            {title}
          </div>
          {applicantCount !== undefined && (
            <span className="substage-hdr-count">({applicantCount})</span>
          )}
        </div>
        {isMenuSupported && (
          <div
            className="substage-hdr-menu"
            ref={menuRef}
            style={{ position: 'relative' }}
            onClick={(e) => { e.stopPropagation(); setMenuOpen(prev => !prev); setRenaming(false); }}
          >
            <i className="fa-solid fa-ellipsis-h"></i>

            {menuOpen && (
              <div className="substage-inline-menu" onClick={(e) => e.stopPropagation()}>
                {/* Reject all — disabled when no job is selected */}
                <button
                  className="substage-inline-menu-item substage-inline-menu-item--danger"
                  onClick={handleRejectAll}
                  disabled={!jobId}
                  style={!jobId ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                >
                  <i className="fa-solid fa-xmark"></i>
                  {t('editSubstage.rejectAll')}
                </button>

                {/* Rename — only for non-static substages */}
                {!isAllApplicants && !isFilterApplicants && (
                  renaming ? (
                    <div className="substage-inline-rename">
                      <input
                        autoFocus
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') setRenaming(false); }}
                        placeholder={t('editSubstage.inputPlaceholder')}
                        className="substage-inline-rename-input"
                      />
                      <button className="substage-inline-menu-item" onClick={handleRename}>
                        <i className="fa-solid fa-check"></i>
                        {t('editSubstage.rename')}
                      </button>
                    </div>
                  ) : (
                    <button
                      className="substage-inline-menu-item"
                      onClick={() => setRenaming(true)}
                    >
                      <i className="fa-solid fa-pencil"></i>
                      {t('editSubstage.rename')}
                    </button>
                  )
                )}

                {['ai-assessment', 'ai-interview'].includes(substageData?.type) && !isFilterApplicants && !isAllApplicants && (
                  <button
                    className="substage-inline-menu-item"
                    onClick={handleEditAssessment}
                  >
                    <i className={`fa-solid ${substageData?.deliveryType === 'two-way-voice' ? 'fa-robot' : substageData?.type === 'ai-interview' ? 'fa-video' : 'fa-robot'}`}></i>
                    {substageData?.deliveryType === 'two-way-voice'
                      ? (t('editSubstage.configureInterviewer') || 'Configure Two-way Interviewer')
                      : substageData?.type === 'ai-interview'
                        ? (t('editSubstage.editInterviewQuestions') || 'Edit Interview Questions')
                      : t('editSubstage.editAssessment')
                    }
                  </button>
                )}

                {substageData?.type === 'ai-assessment' && !isFilterApplicants && !isAllApplicants && (
                  <button
                    className="substage-inline-menu-item"
                    onClick={handleToggleAutoScreening}
                    role="switch"
                    aria-checked={substageData.autoScreeningEnabled === true}
                  >
                    <i className={`fa-solid ${substageData.autoScreeningEnabled === true ? 'fa-toggle-on' : 'fa-toggle-off'}`}></i>
                    {substageData.autoScreeningEnabled === true
                      ? t('editSubstage.autoScreeningOn')
                      : t('editSubstage.autoScreeningOff')}
                  </button>
                )}

                {/* Delete Step — only for dynamic substages (not all-applicants or filter-applicants) */}
                {!isAllApplicants && !isFilterApplicants && (
                  <button
                    className="substage-inline-menu-item substage-inline-menu-item--danger"
                    onClick={handleDelete}
                  >
                    <i className="fa-solid fa-trash"></i>{uiText("Delete Step")}</button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="applicant-container">
        {applicants && applicants.map((applicant) => (
          <ApplicantCell
            key={applicant.id}
            applicant={applicant}
            onDragStart={(e) => handleDragStart(e, applicant.id)}
            onClick={() => onOpenApplicantPopup(applicant)}
            isHired={substageId === 'hired-applicants'}
            isSelected={selectedApplicants.includes(applicant.id)}
            isStarred={applicant.starred || false}
            isOfferApprover={isOfferApprover}
            pendingApprovals={pendingApprovals}
            applicantStatus={applicantStatusMap[applicant.id] || {}}
            substageType={substageType}
            substageAssessmentId={substageAssessmentId}
            neverOpened={!applicant.firstOpenedAt}
          />
        ))}
      </div>
    </div>
  );
};

export default SubstageArea;
